import { FC, memo, useState } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { Document, RequiredDocument } from '../../../../../types/api';
import { maxFileSizeInBytes } from '../../constants';
import bytesToSize from '../../utils/bytesToSize';
import FileDropzone, { FileDropzoneProps } from '../FileDropzone';
import ShowIf from '../ConditionalRender/ShowIf';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

export interface FileInputFieldProps<T extends FieldValues = any>
  extends Omit<
    FileDropzoneProps,
    'file' | 'maxSize' | 'onDropRejected' | 'onDrop' | 'onDropAccepted'
  > {
  touched?: boolean;
  control: Control<T>;
  name: string;
  onUpload: (files: File[]) => Promise<RequiredDocument | Document | void>;
}

const FileInputField: FC<FileInputFieldProps> = ({
  name,
  onUpload,
  control,
  canReplace,
  touched,
  ...rest
}) => {
  const [fileError, setFileError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { value } = field;
        const { error } = fieldState;
        const insights =
          value?.files?.[0]?.OracleInsights?.[0]?.output.validations?.filter(
            (v: any) => v.id == 'is_document_recognized'
          )[0]?.result ?? true;
        const isInvalid = value?.analysis === false || insights === false;
        const inAnalysis = value?.analysis === null;

        return (
          <>
            <FileDropzone
              requiredDocument={value}
              error={fileError || (error as any)?.url?.message}
              loading={loading}
              disabled={loading || (!!value && !canReplace)}
              onDrop={() => setFileError(null)}
              maxSize={maxFileSizeInBytes}
              onDropRejected={(rejectedFiles) => {
                const file = rejectedFiles[0].file;
                const error = rejectedFiles[0].errors[0];
                let errorCopy: string;
                if (error.code === 'file-too-large') {
                  errorCopy = `${
                    file.name
                  } is too big. Max size is ${bytesToSize(
                    maxFileSizeInBytes
                  )}.`;
                } else if (error.code === 'file-invalid-type') {
                  errorCopy = `${file.name} is not a valid file type. Should be an image, PDF, or a text document.`;
                } else {
                  errorCopy = error.message;
                }
                setFileError(errorCopy);
              }}
              onDropAccepted={async (files) => {
                try {
                  setLoading(true);
                  setFileError(null);
                  const document = await onUpload(files);
                  field.onChange(document);
                } catch (e: any) {
                  console.error(e);
                  // Extract error message from backend response
                  let errorMessage = 'Failed to upload file';
                  if (e?.response?.data?.error) {
                    errorMessage = e.response.data.error;
                  } else if (e?.data?.error) {
                    errorMessage = e.data.error;
                  } else if (e?.message) {
                    errorMessage = e.message;
                  }
                  setFileError(errorMessage);
                } finally {
                  setLoading(false);
                }
              }}
              canReplace={canReplace}
              {...rest}
            />
            <ShowIf condition={inAnalysis}>
              <div style={{ color: '#868795', marginTop: '-20px' }}>
                Analyzing file{' '}
                {field.value?.files?.[0]?.file_name?.substr(0, 60)}...
              </div>
            </ShowIf>
            <ShowIf condition={isInvalid}>
              <Box
                sx={{
                  paddingLeft: '1rem',
                  borderLeft: '4px solid #FFCC00',
                  marginTop: '-1rem',
                  marginBottom: '1rem',
                }}
              >
                <Typography variant="caption" fontSize="13px">
                  This may not be a {value?.requirement_name}. Incorrect files
                  can delay the review process. <br />
                  Proceed if you are sure this file is correct.
                </Typography>
              </Box>
            </ShowIf>
          </>
        );
      }}
    />
  );
};

export default memo(FileInputField);
